import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Container,
  Snackbar,
  Alert,
} from '@mui/material';

const ContactBanner: React.FC = () => {
  const [fields, setFields] = useState({
    name: '',
    email: '',
    message: '',
  });

  const [error, setError] = useState('');
  const [toastOpen, setToastOpen] = useState(false);

  const validateEmail = (email: string) =>
    !!email && email.includes('@');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFields({
      ...fields,
      [e.target.name]: e.target.value,
    });

    if (e.target.name === 'email') {
      setError('');
    }
  };

  const handleEmailBlur = () => {
    if (fields.email && !validateEmail(fields.email)) {
      setError('Please enter a valid email address.');
    } else {
      setError('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!fields.name || !fields.email || !fields.message) {
      setError('Please fill in all fields.');
      return;
    }

    if (!validateEmail(fields.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError('');

    setFields({
      name: '',
      email: '',
      message: '',
    });

    setToastOpen(true);
  };

  const fieldStyle = {
    '& .MuiOutlinedInput-root': {
      color: '#fff',
      backgroundColor: 'rgba(8, 20, 34, 0.38)',
      borderRadius: '8px',
      backdropFilter: 'blur(5px)',
      WebkitBackdropFilter: 'blur(5px)',

      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.48)',
      },

      '&:hover fieldset': {
        borderColor: 'rgba(255,255,255,0.8)',
      },

      '&.Mui-focused fieldset': {
        borderColor: '#fff',
        borderWidth: '1px',
      },
    },

    '& .MuiInputLabel-root': {
      color: 'rgba(255,255,255,0.88)',
    },

    '& .MuiInputLabel-root.Mui-focused': {
      color: '#fff',
    },

    '& .MuiFormHelperText-root': {
      color: '#ffb4b4',
      ml: 0,
    },
  };

  return (
    <Box
      id="contact-banner"
      sx={{
        position: 'relative',
        width: '100%',
        py: {
          xs: 7,
          md: 9,
        },
        overflow: 'hidden',
        backgroundColor: '#000',
      }}
    >
      {/* Background image */}
      <Box
        component="img"
        src="/contact2.jpg"
        alt="Shipping terminal"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
        }}
      />

      {/* Background overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(90deg, rgba(1,7,15,0.57) 0%, rgba(1,7,15,0.39) 52%, rgba(1,7,15,0.54) 100%)',
          zIndex: 1,
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
        }}
      >
        {/* Main heading */}
        <Box
          sx={{
            textAlign: 'center',
            mb: {
              xs: 5,
              md: 6,
            },
          }}
        >
          <Typography
            component="h2"
            sx={{
              color: '#fff',
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: {
                xs: '2.1rem',
                md: '2.8rem',
              },
              lineHeight: 1.1,
              mb: 1.5,
            }}
          >
            Get in Touch
          </Typography>

          <Typography
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontFamily:
                '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
              fontSize: {
                xs: '0.95rem',
                md: '1rem',
              },
              maxWidth: 620,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Contact us to explore how we can assist your logistics
            and shipping needs.
          </Typography>
        </Box>

        {/* Form + Map */}
        <Box
          sx={{
            display: 'grid',

            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr 1fr',
            },

            gap: {
              xs: 5,
              md: 6,
            },

            alignItems: 'center',

            maxWidth: 1060,
            mx: 'auto',
          }}
        >
          {/* LEFT - FORM */}
          <Box>
            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={fields.name}
                onChange={handleChange}
                sx={fieldStyle}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                value={fields.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                error={
                  !!error &&
                  error.toLowerCase().includes('email')
                }
                helperText={
                  !!error &&
                  error.toLowerCase().includes('email')
                    ? error
                    : ''
                }
                sx={fieldStyle}
              />

              <TextField
                fullWidth
                label="Message"
                name="message"
                value={fields.message}
                onChange={handleChange}
                multiline
                rows={5}
                sx={fieldStyle}
              />

              {!!error &&
                !error.toLowerCase().includes('email') && (
                  <Alert severity="error">
                    {error}
                  </Alert>
                )}

              <Button
                variant="contained"
                type="submit"
                sx={{
                  mt: 0.5,
                  alignSelf: {
                    xs: 'stretch',
                    sm: 'flex-start',
                  },

                  minWidth: 185,
                  py: 1.3,
                  px: 4,

                  backgroundColor: '#0B5E99',
                  color: '#fff',

                  fontWeight: 700,
                  fontSize: '0.88rem',
                  letterSpacing: '0.035em',

                  borderRadius: '7px',

                  boxShadow:
                    '0 8px 22px rgba(0,0,0,0.22)',

                  '&:hover': {
                    backgroundColor: '#1175B8',
                    boxShadow:
                      '0 10px 28px rgba(0,0,0,0.3)',
                  },
                }}
              >
                Send Message
              </Button>
            </Box>
          </Box>

          {/* RIGHT - ADDRESS + MAP */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 500,
              justifySelf: {
                xs: 'stretch',
                md: 'end',
              },
            }}
          >
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.92)',
                fontFamily:
                  '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
                fontSize: {
                  xs: '0.92rem',
                  md: '0.95rem',
                },
                mb: 1.5,
                textShadow:
                  '0 2px 8px rgba(0,0,0,0.4)',
              }}
            >
              Via XX Settembre, 2/25, 16121 Genova GE, Italy
            </Typography>

            <Box
              sx={{
                width: '100%',
                height: {
                  xs: 330,
                  md: 390,
                },

                borderRadius: '12px',
                overflow: 'hidden',

                boxShadow:
                  '0 16px 38px rgba(0,0,0,0.32)',
              }}
            >
              <iframe
                title="SyncLogistic location in Genova"
                src="https://www.google.com/maps?q=Via%20XX%20Settembre%202%2F25%2C%2016121%20Genova%20GE%2C%20Italy&output=embed"
                width="100%"
                height="100%"
                style={{
                  border: 0,
                  display: 'block',
                }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>
          </Box>
        </Box>

        {/* Success notification */}
        <Snackbar
          open={toastOpen}
          autoHideDuration={3000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          sx={{
            zIndex: 20000,
          }}
        >
          <Alert
            severity="success"
            onClose={() => setToastOpen(false)}
            sx={{
              width: '100%',
            }}
          >
            Thank you for your message! We will get back to you soon.
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ContactBanner;